import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { config, history } from '../_helpers';
import { accountService } from '../_services';
import { toast } from '../_components/Toast';

function ForgotPassword() {
    const [otpSent, setOtpSent] = useState(false);
    const [otpValidated, setOtpValidated] = useState(false);
    const [user, setUser] = useState(null);
    const [timer, setTimer] = useState(180); // 3 minutes in seconds
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        let interval = null;
        if (otpSent && !otpValidated && timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setCanResend(true);
            if (interval) clearInterval(interval);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [otpSent, otpValidated, timer]);

    function handleResendOTP(email) {
        setCanResend(false);
        setTimer(180);
        accountService.forgotPassword(email)
            .then(() => toast.success('OTP resent to email.'))
            .catch(error => toast.error(error));
    }

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Email is invalid')
            .required('Email is required'),
        otp: otpSent && !otpValidated ? Yup.string().required('OTP is required') : Yup.string(),
        password: otpValidated ? Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required') : Yup.string(),
        confirmPassword: otpValidated ? Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm Password is required') : Yup.string()
    });

    function onSubmit({ email, otp, password, confirmPassword }, { setSubmitting }) {
        toast.clear();
        if (!otpSent) {
            accountService.forgotPassword(email)
                .then(() => {
                    toast.success('OTP sent to email.');
                    setOtpSent(true);
                    setTimer(180);
                    setCanResend(false);
                })
                .catch(error => toast.error(error))
                .finally(() => setSubmitting(false));
        } else if (!otpValidated) {
            accountService.validateOTP(email, otp)
                .then(res => {
                    console.log('res', res);
                    if (res.valid && res.user && res.token) {
                        toast.success('OTP validated successfully. Please set your new password.');
                        setOtpValidated(true);
                        setUser(res.user);
                        if (res.token) {
                            sessionStorage.setItem('jwtToken', res.token);
                        }
                    } else {
                        toast.error('OTP not valid or user not found.');
                    }
                })
                .catch(error => toast.error(error))
                .finally(() => setSubmitting(false));
        } else {
            // Use the resetPassword service to update password
            const token = sessionStorage.getItem('jwtToken');
            accountService.resetPassword({ token, password, confirmPassword, user })
                .then(() => {
                    toast.success('Password reset successful! You can now sign in with your new password.');
                    // Clear the temporary token
                    sessionStorage.removeItem('jwtToken');
                    // Redirect to login
                    history.push('login');
                })
                .catch(error => toast.error(error))
                .finally(() => setSubmitting(false));
        }
    }

    return (
        <Formik
            initialValues={{ email: '', otp: '', password: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize={true}
        >
            {({ values, errors, touched, isSubmitting }) => (
                <Form>
                    <h5 className="card-header">Forgot Password</h5>

                    {!otpValidated && (
                        <>
                            <div className="form-group">
                                <label>Email Address</label>
                                <Field
                                    name="email"
                                    type="text"
                                    disabled={otpSent}
                                    className={'form-control' + (errors.email && touched.email ? ' is-invalid' : '')}
                                    placeholder="Enter your email"
                                />
                                <ErrorMessage name="email" component="div" className="invalid-feedback" />
                            </div>

                            {otpSent && (
                                <div className="form-group">
                                    <label>Enter OTP</label>
                                    <Field
                                        name="otp"
                                        type="text"
                                        className={'form-control' + (errors.otp && touched.otp ? ' is-invalid' : '')}
                                        placeholder="Enter the OTP sent to your email"
                                    />
                                    <ErrorMessage name="otp" component="div" className="invalid-feedback" />

                                    <div className="text-center mt-2">
                                        {canResend ? (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => handleResendOTP(values.email)}
                                            >
                                                Resend OTP
                                            </button>
                                        ) : (
                                            <small className="text-muted">Resend OTP in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</small>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {otpValidated && (
                        <>
                            <div className="form-group">
                                <label>New Password</label>
                                <Field
                                    name="password"
                                    type="password"
                                    className={'form-control' + (errors.password && touched.password ? ' is-invalid' : '')}
                                    placeholder="New Password"
                                />
                                <ErrorMessage name="password" component="div" className="invalid-feedback" />
                            </div>

                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <Field
                                    name="confirmPassword"
                                    type="password"
                                    className={'form-control' + (errors.confirmPassword && touched.confirmPassword ? ' is-invalid' : '')}
                                    placeholder="Confirm Password"
                                />
                                <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
                            </div>
                        </>
                    )}

                    <div className="form-group mt-3">
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary w-100">
                            {isSubmitting && <span className="spinner-border spinner-border-sm mr-1"></span>}
                            {!otpSent ? 'Send OTP' : (!otpValidated ? 'Verify OTP' : 'Reset Password')}
                        </button>
                    </div>

                    <div className="form-group d-flex justify-content-center align-items-center mt-4">
                        <Link to="login" className="btn btn-link p-0">
                            Back to Sign In
                        </Link>
                    </div>

                    <div style={{ fontSize: "0.7em", color: "#666", textAlign: "center", marginTop: "30px" }}>
                        <span>&#169;</span>{new Date().getFullYear() + " " + config.Provider}. All Rights Reserved.
                    </div>
                </Form>
            )}
        </Formik>
    )
}

export { ForgotPassword };